import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { isPromise } from "@insightfulscience/shared-utils/fn";
import displayName from "../../displayName";
import diContext from "./context";
import NotSerializableProp from "../../hoc-merge-props";

const { merge, extract } = NotSerializableProp("container");

const useIsomorphicEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

const DiPageHoC = createContainer => Page => {
	const DiPage = pageProps => {
		const [containerFromProps, data] = extract(pageProps);
		const container = useMemo(
			() => containerFromProps ?? createContainer(null),
			[], // create container once
		);
		const [state, setState] = useState(data);
		const stateRef = useRef();

		stateRef.current = state;

		const updateState = useCallback(
			statePatch => {
				const newState =
					typeof statePatch === "function" ? statePatch(stateRef.current) : statePatch;
				stateRef.current = newState;
				setState(newState);
			},
			[setState],
		);

		useIsomorphicEffect(() => {
			if (state === data) return;
			updateState(data);
		}, [pageProps]);

		const run = React.useCallback(
			fn => fn(container, stateRef.current, run, updateState), // run fn in the di-context
			[container], // deps
		);

		return (
			<diContext.Provider value={{ run, data: state, container, setState: updateState }}>
				<Page {...data} />
			</diContext.Provider>
		);
	};

	Object.assign(DiPage, Page); // copying all static props

	DiPage.displayName = displayName("withDiContext", Page);

	DiPage.getInitialProps = context => {
		const container = createContainer(context);

		const runGetInitProps = fn => fn(context, container, runGetInitProps);

		const props =
			typeof Page.getInitialProps === "function" ? runGetInitProps(Page.getInitialProps) : null;

		return isPromise(props)
			? props.then(resolved => merge(resolved, container))
			: merge(props, container);
	};

	return DiPage;
};

export default DiPageHoC;
